import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const NEWSPAPER = "/assets/newspaper.png";

const PILLARS = [
  { number: "01", title: "Define",   body: "We begin by understanding — not designing. Your world, your voice, your quiet contradictions." },
  { number: "02", title: "Design",   body: "Tailored, never off-the-shelf. Spaces, wardrobes, brands and rituals — composed with restraint." },
  { number: "03", title: "Detail",   body: "From first mood-board to final finish, we hold every seam. One editor's eye, a hundred hands." },
  { number: "04", title: "Discreet", body: "Confidentiality is not policy — it is posture. Your name, your world, stay yours." },
];

export default function Philosophy() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // The blurred newspaper drifts slowly, giving continuity from the hero
  const bgY = useTransform(scrollYProgress, [0, 1], [-60, 60]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.15, 0.6, 1], [0.35, 0.28, 0.12, 0.04]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  return (
    <section
      id="philosophy"
      ref={ref}
      data-testid="philosophy-section"
      className="relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #0F110E 0%, #0A0B08 55%, #050604 88%, #000000 100%)",
      }}
    >
      {/* Continued newspaper — heavily blurred, like the "background" from the newspaper he's holding */}
      <motion.div
        aria-hidden
        style={{ y: bgY, opacity: bgOpacity, scale: bgScale }}
        className="absolute inset-0 pointer-events-none"
      >
        <img
          src={NEWSPAPER}
          alt=""
          className="w-full h-full object-cover"
          style={{ filter: "blur(48px) saturate(0.75) brightness(0.75)" }}
          draggable={false}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 90% 60% at 50% 45%, rgba(15,17,14,0.35), rgba(15,17,14,0.85))",
          }}
        />
      </motion.div>

      {/* Soft brass ambient */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 50% 30% at 25% 40%, rgba(176,140,74,0.10), transparent 60%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 py-28 md:py-40">
        {/* Overline + heading */}
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-3 text-brass mb-8"
          >
            <span className="w-8 h-px bg-brass" />
            <span className="text-[10px] md:text-[11px] tracking-overline uppercase font-medium">
              The Studio's Philosophy
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 1.1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif font-light tracking-editorial leading-[0.94] text-cream"
            style={{ fontSize: "clamp(48px, 7vw, 128px)" }}
          >
            A celebration<br />
            of <span className="italic text-brass">identity.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-2xl text-lg md:text-xl text-cream/80 leading-relaxed font-light"
          >
            Design, to us, is the visible extension of who you are. Every space
            we shape, every mark we make, every ritual we curate exists to
            honour a single idea: your identity, in higher resolution.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 max-w-2xl text-base md:text-lg text-cream/60 leading-relaxed font-light italic"
          >
            What follows is <span className="not-italic text-cream/85">how</span> we hold that promise — a
            simple, four‑act method we bring to every engagement.
          </motion.p>
        </div>

        {/* Four D's pillars — tighter, ledger style */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-4 gap-x-8 md:gap-x-10 gap-y-14 border-t border-cream/12 pt-10 md:pt-14">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`philosophy-pillar-${p.title.toLowerCase()}`}
              className="group relative"
            >
              <div className="absolute -top-[41px] left-0 h-px w-10 bg-brass transition-all duration-700 group-hover:w-24" />
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-[10px] tracking-overline uppercase text-brass font-medium">{p.number}</span>
                <span className="text-[10px] tracking-overline uppercase text-cream/40">Act</span>
              </div>
              <h3 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-editorial leading-[0.95] text-cream">
                {p.title}
                <span className="text-brass">.</span>
              </h3>
              <p className="mt-5 text-cream/65 text-[14px] md:text-[15px] leading-relaxed font-light">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
